import React from "react";
import { Typography } from "@mui/material";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot, LineHighlightPlot } from "@mui/x-charts/LineChart";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { ChartsClipPath } from "@mui/x-charts/ChartsClipPath";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";

import { HubCardStack } from "../../components";

import { DailyChartTooltip } from "./DailyChartTooltip";

import {
  GAP_LINE_COLOR,
  WEEK_COLORS,
  buildDataset,
  buildWeekColors,
  formatCurrency,
} from "./helper";

const DepartmentChart = ({ department, days, weekLabels, goal = 45000 }) => {
  const dataset = buildDataset(department, days, goal);
  const clipId = `daily-chart-clip-${department.id}`;

  const TooltipContent = (props) => (
    <DailyChartTooltip {...props} days={days} weekLabels={weekLabels} />
  );

  return (
    <HubCardStack
      padding="10px"
      className="print-break-avoid"
      sx={{ ".MuiLineElement-root": { fill: "#80808050" } }}
    >
      <Typography variant="caption" pl="15px">
        {department.label}
      </Typography>
      <ResponsiveChartContainer
        height={350}
        dataset={dataset}
        margin={{ top: 10, right: 20, bottom: 30, left: 60 }}
        xAxis={[
          {
            id: "day",
            scaleType: "band",
            dataKey: "day",
            valueFormatter: (day) => weekLabels[day] || "",
            tickPlacement: "middle",
            tickInterval: (day) => Boolean(weekLabels[day]),
            colorMap: {
              type: "ordinal",
              values: days,
              colors: buildWeekColors(days),
            },
          },
        ]}
        yAxis={[{ id: "revenue", valueFormatter: formatCurrency }]}
        series={[
          {
            type: "bar",
            dataKey: "revenue",
            label: "Completed Revenue",
            color: WEEK_COLORS[0],
            yAxisId: "revenue",
            valueFormatter: (v) => formatCurrency(v),
          },
          {
            type: "line",
            dataKey: "gap",
            label: "Cumulative Gap",
            color: GAP_LINE_COLOR,
            showMark: false,
            connectNulls: false,
            valueFormatter: formatCurrency,
          },
        ]}
      >
        <ChartsGrid horizontal />
        <g clipPath={`url(#${clipId})`}>
          <LinePlot />
          <BarPlot
            slotProps={{
              bar: { clipPath: "inset(0px round 4px 4px 0px 0px)" },
            }}
          />
          <ChartsAxisHighlight x="band" />
        </g>
        <MarkPlot />
        <LineHighlightPlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="revenue" />
        {goal ? (
          <ChartsReferenceLine
            y={goal}
            yAxisId="revenue"
            label={`Goal ${formatCurrency(goal)}`}
            labelAlign="end"
            lineStyle={{
              stroke: GAP_LINE_COLOR,
              // strokeDasharray: "4 3",
              strokeWidth: 1,
            }}
            labelStyle={{ fontSize: 10, fill: GAP_LINE_COLOR }}
          />
        ) : null}
        <ChartsTooltip trigger="axis" axisContent={TooltipContent} />
        <ChartsClipPath id={clipId} />
      </ResponsiveChartContainer>
    </HubCardStack>
  );
};

export default DepartmentChart;
